import { useState } from 'react'
import { AlertTriangle, Pencil, Plus, PowerOff } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useEstablishments, useLegalEntities, useOrgAdminMutations } from '../api'
import { BANK_CHANNELS } from '../types'
import type { BankChannel, EstablishmentView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

type Draft = {
  id?: string
  legalEntityId: string
  code: string
  name: string
  mohreEstablishmentId: string
  wpsEmployerId: string
  bankChannel: BankChannel | ''
  bankName: string
  routingCode: string
  licenceNumber: string
  licenceExpiry: string
  address: string
  active: boolean
}

const EMPTY: Draft = {
  legalEntityId: '', code: '', name: '', mohreEstablishmentId: '', wpsEmployerId: '',
  bankChannel: '', bankName: '', routingCode: '', licenceNumber: '', licenceExpiry: '',
  address: '', active: true,
}

/** Trade licences inside this window are flagged; renewal is a PRO case in part 5c. */
const EXPIRY_WARNING_DAYS = 90

function expiringSoon(date: string | null): boolean {
  if (!date) return false
  const days = (new Date(date).getTime() - Date.now()) / 86_400_000
  return days <= EXPIRY_WARNING_DAYS
}

/**
 * Establishments — the MOHRE registration a WPS file is generated against.
 *
 * <p>One legal entity may hold several, and every assignment points at one. Like divisions,
 * the table has existed since V11 with no way to change it outside a migration.
 */
export function EstablishmentsPage() {
  useBreadcrumb()
  const establishments = useEstablishments()
  const entities = useLegalEntities()
  const { createEstablishment, updateEstablishment, deactivateEstablishment } = useOrgAdminMutations()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [toDeactivate, setToDeactivate] = useState<EstablishmentView | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!draft) return
    setError(null)
    const body = {
      legalEntityId: draft.legalEntityId || null,
      code: draft.code,
      name: draft.name,
      mohreEstablishmentId: draft.mohreEstablishmentId || null,
      wpsEmployerId: draft.wpsEmployerId || null,
      bankChannel: draft.bankChannel || null,
      bankName: draft.bankName || null,
      routingCode: draft.routingCode || null,
      licenceNumber: draft.licenceNumber || null,
      licenceExpiry: draft.licenceExpiry || null,
      address: draft.address || null,
      active: draft.active,
    }
    try {
      if (draft.id) {
        await updateEstablishment.mutateAsync({ id: draft.id, ...body })
      } else {
        await createEstablishment.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That establishment could not be saved')
    }
  }

  function edit(establishment: EstablishmentView) {
    setError(null)
    setDraft({
      id: establishment.id,
      legalEntityId: establishment.legalEntityId ?? '',
      code: establishment.code,
      name: establishment.name,
      mohreEstablishmentId: establishment.mohreEstablishmentId ?? '',
      wpsEmployerId: establishment.wpsEmployerId ?? '',
      bankChannel: establishment.bankChannel ?? '',
      bankName: establishment.bankName ?? '',
      routingCode: establishment.routingCode ?? '',
      licenceNumber: establishment.licenceNumber ?? '',
      licenceExpiry: establishment.licenceExpiry ?? '',
      address: establishment.address ?? '',
      active: establishment.active,
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Establishments"
        subtitle="MOHRE registrations. A WPS file is generated per establishment, not per entity."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            Add an establishment
          </Button>
        }
      />

      <Card>
        {establishments.isPending && <Skeleton className="m-4 h-32" />}
        {establishments.isError && (
          <ErrorState error={establishments.error} onRetry={() => void establishments.refetch()} />
        )}
        {establishments.isSuccess && establishments.data.length === 0 && (
          <EmptyState
            title="No establishments yet"
            description="Add the MOHRE registration each group of employees is filed under."
          />
        )}
        {(establishments.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[54rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Name</th>
                  <th scope="col" className="p-3 font-medium">Code</th>
                  <th scope="col" className="p-3 font-medium">MOHRE id</th>
                  <th scope="col" className="p-3 font-medium">Pay channel</th>
                  <th scope="col" className="p-3 font-medium">Trade licence</th>
                  <th scope="col" className="p-3 text-right font-medium">People</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {establishments.data?.map((establishment) => (
                  <tr key={establishment.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">{establishment.name}</td>
                    <td className="p-3"><code className="text-xs">{establishment.code}</code></td>
                    <td className="p-3 text-[var(--color-muted)]">{establishment.mohreEstablishmentId ?? '—'}</td>
                    <td className="p-3 text-[var(--color-muted)]">
                      {establishment.bankChannel
                        ? BANK_CHANNELS.find((c) => c.value === establishment.bankChannel)?.label
                        : '—'}
                    </td>
                    <td className="p-3">
                      {establishment.licenceNumber ? (
                        <span className="flex items-center gap-1.5">
                          <span>{establishment.licenceNumber}</span>
                          {establishment.licenceExpiry && (
                            <span
                              className={
                                expiringSoon(establishment.licenceExpiry)
                                  ? 'flex items-center gap-1 text-xs text-[var(--color-warning)]'
                                  : 'text-xs text-[var(--color-muted)]'
                              }
                            >
                              {expiringSoon(establishment.licenceExpiry) && (
                                <AlertTriangle className="size-3.5" aria-hidden="true" />
                              )}
                              <DateValue value={establishment.licenceExpiry} />
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[var(--color-muted)]">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums">{establishment.assignedCount}</td>
                    <td className="p-3"><StatusBadge status={establishment.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" size="icon" variant="ghost"
                                aria-label={`Edit ${establishment.name}`}
                                onClick={() => edit(establishment)}>
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {establishment.active && (
                          <Button type="button" size="icon" variant="ghost"
                                  aria-label={`Deactivate ${establishment.name}`}
                                  onClick={() => setToDeactivate(establishment)}>
                            <PowerOff className="size-4" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
        title={draft?.id ? `Edit ${draft.name}` : 'Add an establishment'}
        description="Every assignment points at one of these, and WPS files are generated per establishment."
        size="lg"
        loading={createEstablishment.isPending || updateEstablishment.isPending}
        error={error}
        onSubmit={() => void save()}
      >
        {draft && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="est-name" required>Name</Label>
                <Input id="est-name" required value={draft.name}
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est-code" required>Code</Label>
                <Input id="est-code" required value={draft.code}
                       onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="est-entity" required>Legal entity</Label>
              {/* A picker, not a text box: the API takes the entity's id, and a typed name
                  reached it as an unparseable UUID ("JSON parse error ... UUID"). */}
              <Select
                id="est-entity" required value={draft.legalEntityId}
                onChange={(e) => setDraft({ ...draft, legalEntityId: e.target.value })}
                className="w-full"
              >
                <option value="" disabled>
                  {entities.isLoading ? 'Loading entities…' : 'Select a legal entity'}
                </option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
              <p className="text-xs text-[var(--color-muted)]">
                An establishment belongs to exactly one entity — that is what a WPS file is filed under.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="est-mohre">MOHRE establishment id</Label>
                <Input id="est-mohre" value={draft.mohreEstablishmentId}
                       onChange={(e) => setDraft({ ...draft, mohreEstablishmentId: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est-wps">WPS employer id</Label>
                <Input id="est-wps" value={draft.wpsEmployerId}
                       onChange={(e) => setDraft({ ...draft, wpsEmployerId: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="est-channel">Pay channel</Label>
                <Select
                  id="est-channel" value={draft.bankChannel}
                  onChange={(e) => setDraft({ ...draft, bankChannel: e.target.value as BankChannel | '' })}
                  className="w-full"
                >
                  <option value="">Not set</option>
                  {BANK_CHANNELS.map((channel) => (
                    <option key={channel.value} value={channel.value}>{channel.label}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est-bank">Bank</Label>
                <Input id="est-bank" value={draft.bankName}
                       onChange={(e) => setDraft({ ...draft, bankName: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est-routing">Routing code</Label>
                <Input id="est-routing" value={draft.routingCode}
                       onChange={(e) => setDraft({ ...draft, routingCode: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="est-licence">Trade licence number</Label>
                <Input id="est-licence" value={draft.licenceNumber}
                       onChange={(e) => setDraft({ ...draft, licenceNumber: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="est-expiry">Licence expiry</Label>
                <DatePicker id="est-expiry" value={draft.licenceExpiry}
                       onChange={(e) => setDraft({ ...draft, licenceExpiry: e.target.value })} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="est-address">Address</Label>
              <Input id="est-address" value={draft.address}
                     onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
            </div>
          </>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toDeactivate !== null}
        onOpenChange={(open) => !open && setToDeactivate(null)}
        title="Deactivate this establishment?"
        description={
          toDeactivate
            ? toDeactivate.assignedCount > 0
              ? `${toDeactivate.assignedCount} person(s) are still assigned to ${toDeactivate.name}. This will be refused until they are moved.`
              : `${toDeactivate.name} stops being offered for new assignments. Existing history keeps referring to it.`
            : undefined
        }
        confirmLabel="Deactivate"
        destructive
        loading={deactivateEstablishment.isPending}
        onConfirm={() => {
          if (toDeactivate) {
            deactivateEstablishment.mutate(toDeactivate.id, { onSettled: () => setToDeactivate(null) })
          }
        }}
      />
    </div>
  )
}
